import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ServiceItem from '../components/ServiceItem';

const Services = () => {
  const location = useLocation();
  
  // This effect handles the scrolling to the proper section when navigating with hash
  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      // Get the element by ID (without the #)
      const element = document.getElementById(location.hash.substring(1));
      
      if (element) {
        // Wait a bit for the DOM to fully render
        setTimeout(() => {
          // Scroll to the element with offset for the fixed header
          window.scrollTo({
            top: element.offsetTop - 100, // Adjust this offset based on your header height
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [location]);

  const renderServiceItems = (items, category) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item, index) => (
        <ServiceItem key={index} item={item} category={category} />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Services Banner */}
      <div className="bg-aqua-600 text-white py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold mb-4">Our Services</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Discover our premium event rental services designed to make your special occasion unforgettable
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="container mx-auto px-4 py-16">

        {/* Chilanga Mulilo Rentals Section */}
        <div id="chilanga" className="mt-16 bg-white rounded-lg shadow-lg p-8 scroll-mt-32">
          <h2 className="text-3xl font-playfair font-bold mb-6 text-center">
            Chilanga Mulilo Rental Items
          </h2>
          {renderServiceItems([
            'Serving pots',
            'Extra large Cooking pots',
            'Extra large Braziers',
            'Extra large cooking sticks',
            'Clear plastic buckets',
            'Extra large Aluminium basins',
            'Small Aluminium basins'
          ], 'chilanga-mulilo')}
        </div>

        {/* Event Décor Items Section */}
        <div id="decor" className="mt-16 bg-white rounded-lg shadow-lg p-8 scroll-mt-32">
          <h2 className="text-3xl font-playfair font-bold mb-6 text-center">
            Event Décor Items
          </h2>
          {renderServiceItems([
            'Premium Table Linens',
            'Elegant Chair Covers',
            'Luxury Table Runners',
            'Designer Centerpieces',
            'Artificial Flower Arrangements',
            'Decorative Backdrops',
            'LED Light Installations',
            'Table Number Holders',
            'Elegant Vases & Containers'
          ], 'decor')}
        </div>

        {/* Music System Section */}
        <div id="music-system" className="mt-16 bg-white rounded-lg shadow-lg p-8 scroll-mt-32">
          <h2 className="text-3xl font-playfair font-bold mb-6 text-center">
            Music System
          </h2>
          {renderServiceItems([
            'Professional PA Speakers',
            'Digital Mixing Consoles',
            'Professional DJ Services',
            'Wireless Microphones',
            'Stage Monitors',
            'LED DJ Booth',
            'Subwoofers',
            'Audio Interface Systems',
            'Professional Lighting'
          ], 'music-system')}
        </div>

        {/* Photography Section */}
        <div id="photography" className="mt-16 bg-white rounded-lg shadow-lg p-8 scroll-mt-32">
          <h2 className="text-3xl font-playfair font-bold mb-6 text-center">
            Photography Services
          </h2>
          {renderServiceItems([
            '360° Photo Podium',
            'High-Definition Cameras',
            'Professional Lighting Kit',
            'Drone Photography',
            'Photo Booth Setup',
            'Instant Photo Printing',
            'Green Screen Background',
            'Professional Flash Systems',
            'Photography Backdrop Stand'
          ], 'photography')}
        </div>

        {/* Catering Section */}
        <div id="catering" className="mt-16 bg-white rounded-lg shadow-lg p-8 scroll-mt-32">
          <h2 className="text-3xl font-playfair font-bold mb-6 text-center">
            Catering Equipment
          </h2>
          {renderServiceItems([
            'Chafing Dishes',
            'Warming Stations',
            'Serving Platters & Utensils',
            'Coffee/Tea Service Sets',
            'Beverage Dispensers',
            'Food Display Stands',
            'Cake Display Stands',
            'Ice Buckets & Tongs',
            'Buffet Tables & Linens'
          ], 'catering')}
        </div>

        {/* Call to Action Section */}
        <div className="mt-16 text-center">
          <h2 className="text-3xl font-playfair font-bold mb-4">
            Need a Custom Package?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Don't see exactly what you're looking for? Contact us to discuss your specific requirements.
            We're happy to create a custom solution for your event.
          </p>
          <button 
            onClick={() => window.location.href = '/contact'}
            className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gold-500 hover:bg-gold-600 transition duration-300"
          >
            Contact Us
          </button>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Quality Promise */}
            <div className="text-center">
              <div className="bg-white rounded-full p-6 w-20 h-20 mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-aqua-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-playfair font-bold mb-2">Quality Promise</h3>
              <p className="text-gray-600">
                All our equipment is meticulously maintained and cleaned before every event
              </p>
            </div>

            {/* Professional Setup */}
            <div className="text-center">
              <div className="bg-white rounded-full p-6 w-20 h-20 mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-aqua-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-playfair font-bold mb-2">Professional Setup</h3>
              <p className="text-gray-600">
                Expert installation and arrangement of all rented items
              </p>
            </div>

            {/* 24/7 Support */}
            <div className="text-center">
              <div className="bg-white rounded-full p-6 w-20 h-20 mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-aqua-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-playfair font-bold mb-2">24/7 Support</h3>
              <p className="text-gray-600">
                Available round the clock for any assistance during your event
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;